<script setup lang="ts">
import Simplebar from "simplebar-vue";
import { computed, ref, useTemplateRef, watch } from "vue";
import { useRoute } from "vue-router";

import ParetoDiagram, { type ParetoPoint } from "@/components/datascience/ParetoDiagram.vue";
import DropdownButton from "@/components/ui/DropdownButton.vue";
import DropdownButtonItem from "@/components/ui/DropdownButtonItem.vue";
import FloatingTooltip from "@/components/ui/FloatingTooltip.vue";
import MarkdownWidget from "@/components/ui/MarkdownWidget.vue";
import SimpleButton from "@/components/ui/SimpleButton.vue";
import type { EstimatorReportDto } from "@/dto";
import { RouteNames } from "@/router";
import { smartToFixed } from "@/services/utils";
import { useModalsStore } from "@/stores/modals";

const { reports, isReadOnly } = defineProps<{
  reports: EstimatorReportDto[];
  isReadOnly: boolean;
}>();

const route = useRoute();
const modal = useModalsStore();
const tenantName = computed(() => route.params.tenantName as string);
const projectName = computed(() => route.params.projectName as string);

const xMetric = ref("predict_time");
const yMetric = ref("");
const hoveredModelId = ref(-1);
const tooltipPosition = ref({ x: -1, y: -1 });
const selectedModelsIds = defineModel<number[]>("selectedModelsIds", { required: true });
const tradeoffExplanations = defineModel<{ reportId: string; explanation: string }[]>(
  "tradeoffExplanations",
  { required: true }
);
const paretoDiagram = useTemplateRef<InstanceType<typeof ParetoDiagram>>("paretoDiagram");
let hideTooltipTimeout = -1;

const metricsNames = computed(() => [
  ...new Set(
    reports
      .flatMap((r) => r.metrics)
      .sort((a, b) => (b.position ?? -Infinity) - (a.position ?? -Infinity))
      .map((m) => m.name)
  ),
]);
const metricsVerboseNamesMap = computed(() => {
  const namesMap = new Map<string, string>();
  reports
    .flatMap((r) => r.metrics)
    .forEach((m) => {
      namesMap.set(m.name, m.verbose_name);
    });
  return namesMap;
});
const metricsGreaterIsBetterMap = computed(() => {
  const favorabilityMap = new Map<string, boolean>();
  reports
    .flatMap((r) => r.metrics)
    .forEach((m) => {
      favorabilityMap.set(m.name, m.greater_is_better ?? true);
    });
  return favorabilityMap;
});
const metricsDifferencesMap = computed(() => {
  const differencesMap = new Map<string, Map<string, number>>();
  const greaterIsBetter = metricsGreaterIsBetterMap.value.get(yMetric.value);
  const best = [...reports].sort((a, b) => {
    const aValue = getReportMetricByName(a, yMetric.value);
    const bValue = getReportMetricByName(b, yMetric.value);
    if (greaterIsBetter) {
      return bValue - aValue;
    }
    return aValue - bValue;
  })[0];
  if (best) {
    for (const r of reports) {
      const differences = new Map<string, number>();
      for (const m of metricsNames.value) {
        const bestValue = getReportMetricByName(best, m);
        const value = getReportMetricByName(r, m);
        differences.set(m, value - bestValue);
      }
      differencesMap.set(r.key, differences);
    }
  }
  return differencesMap;
});
const pointsOnPlot = computed<ParetoPoint[]>(() =>
  reports
    .map((r) => ({
      id: r.id,
      x: getReportMetricByName(r, xMetric.value),
      y: getReportMetricByName(r, yMetric.value),
      hover: r.id === hoveredModelId.value,
    }))
    .filter((p) => !isNaN(p.x) && !isNaN(p.y))
);
const directions = computed(() => ({
  x: metricsGreaterIsBetterMap.value.get(xMetric.value) ?? true,
  y: metricsGreaterIsBetterMap.value.get(yMetric.value) ?? true,
}));
const selectedModels = computed(() =>
  selectedModelsIds.value.map((id) => reports.find((r) => r.id === id)!)
);

function getReportMetricByName(report: EstimatorReportDto, metricName: string) {
  const name = metricsVerboseNamesMap.value.get(metricName);
  if (name) {
    const m = report.metrics.find(
      (m) => (m.data_source === "test" || m.data_source === null) && m.name === metricName
    );
    if (m) {
      return m.value;
    }
  }
  return NaN; // 🤷
}

function getFormattedReportMetricByName(report: EstimatorReportDto, metricName: string) {
  const m = getReportMetricByName(report, metricName);
  if (isNaN(m)) {
    return "--"; // 🤷
  }
  return smartToFixed(m);
}

function getFormattedMetricDifference(report: EstimatorReportDto, metricName: string) {
  const v = metricsDifferencesMap.value.get(report.key)?.get(metricName) ?? NaN;
  const greaterIsBetter = metricsGreaterIsBetterMap.value.get(metricName);
  const prefix = v > 0 ? "+" : "";
  return {
    better: greaterIsBetter ? v > 0 : v < 0,
    stringValue: `${prefix}${smartToFixed(v)}`,
    value: v,
  };
}

async function toggleModel(id: number) {
  if (selectedModelsIds.value.includes(id)) {
    selectedModelsIds.value = selectedModelsIds.value.filter((i) => i !== id);
  } else {
    const [confirmed, explanation] = await modal.multilinePrompt({
      title: "Explain why this trade-off",
    });
    if (confirmed) {
      tradeoffExplanations.value.push({ reportId: id.toString(), explanation });
      selectedModelsIds.value.push(id);
    }
  }
}

function getExplanation(reportId: number) {
  const explanation = tradeoffExplanations.value.find((e) => e.reportId === reportId.toString());
  return explanation?.explanation ?? "";
}

function getHoveredReportName(reportId: number) {
  const r = reports.find((r) => r.id == reportId);
  if (r) {
    return r.key;
  }
  return "";
}

function onMouseOverPoint(id: number, x: number, y: number) {
  clearTimeout(hideTooltipTimeout);
  hoveredModelId.value = id;
  tooltipPosition.value = { x, y };
}

function onMouseOut() {
  hideTooltipTimeout = window.setTimeout(() => {
    hoveredModelId.value = -1;
    tooltipPosition.value = { x: -1, y: -1 };
  }, 200);
}

function onMouseHoverReport(id: number) {
  const r = reports.find((r) => r.id == id);
  if (r) {
    const x = getReportMetricByName(r, xMetric.value);
    const y = getReportMetricByName(r, yMetric.value);
    if (!isNaN(x) && !isNaN(y)) {
      clearTimeout(hideTooltipTimeout);
      hoveredModelId.value = id;
      if (paretoDiagram.value && id !== -1) {
        tooltipPosition.value = paretoDiagram.value.getCoordinates(id);
      }
    }
  }
}

watch(
  () => reports,
  () => {
    yMetric.value = metricsNames.value[0];
  },
  { immediate: true }
);
</script>

<template>
  <div class="estimator-report-comparison">
    <div class="plot">
      <div class="block-title">
        <div class="label-s"><i class="title-icon icon icon-bar-chart-2" /> Pareto diagram</div>
        <div class="actions">
          <DropdownButton help-label="x: " :label="xMetric" icon-left="icon-chevron-down">
            <DropdownButtonItem
              v-for="m in metricsNames"
              :key="m"
              :label="metricsVerboseNamesMap.get(m)"
              @click="xMetric = m"
            />
          </DropdownButton>
          <DropdownButton help-label="y: " :label="yMetric" icon-left="icon-chevron-down">
            <DropdownButtonItem
              v-for="m in metricsNames"
              :key="m"
              :label="metricsVerboseNamesMap.get(m)"
              @click="yMetric = m"
            />
          </DropdownButton>
        </div>
      </div>
      <div class="plot-container">
        <ParetoDiagram
          ref="paretoDiagram"
          :points="pointsOnPlot"
          :directions="directions"
          @mouse-over-point="onMouseOverPoint"
          @mouse-out-point="onMouseOut"
        />
        <Transition name="fade">
          <div
            v-if="hoveredModelId !== -1 && tooltipPosition.x !== -1 && tooltipPosition.y !== -1"
            class="plot-tooltip"
            :style="{
              transform: `translate(${tooltipPosition.x + 20}px, ${tooltipPosition.y - 20}px)`,
            }"
          >
            {{ getHoveredReportName(hoveredModelId) }}
          </div>
        </Transition>
      </div>
    </div>
    <div class="selected-models">
      <div class="block-title">
        <div class="label-s">
          <i class="title-icon icon icon-selected-cube" /> Selected trade-offs
        </div>
      </div>
      <div class="selected-models-container">
        <Transition name="fade" mode="out-in">
          <div v-if="selectedModelsIds.length === 0" class="placeholder">
            <div class="label-l">No model selected</div>
            <div class="label-s">Select one’s you want to save in the table below.</div>
          </div>
          <Simplebar v-else class="scroller">
            <div class="selected-models-list">
              <TransitionGroup name="list">
                <div
                  v-for="model in selectedModels"
                  :key="model.id"
                  class="selected-model-container"
                >
                  <div
                    :class="{ hover: model.id === hoveredModelId }"
                    @mouseover="onMouseHoverReport(model.id)"
                    @mouseout="onMouseOut"
                    class="selected-model"
                  >
                    <div class="name-and-score">
                      <div class="name label-s"><i class="icon icon-cube" /> {{ model.key }}</div>
                      <div class="metrics">
                        <div class="label-s">
                          {{ getFormattedReportMetricByName(model, xMetric) }}
                          <span>({{ metricsVerboseNamesMap.get(xMetric) }})</span>
                        </div>
                        <div class="label-s">
                          {{ getFormattedReportMetricByName(model, yMetric) }}
                          <span>({{ metricsVerboseNamesMap.get(yMetric) }})</span>
                        </div>
                      </div>
                    </div>
                    <div class="explanation paragraph-s">
                      <div><i class="icon icon-document" /></div>
                      <div class="text">
                        <MarkdownWidget :source="getExplanation(model.id)" />
                      </div>
                    </div>
                  </div>
                </div>
              </TransitionGroup>
            </div>
          </Simplebar>
        </Transition>
      </div>
    </div>
    <div class="all-models">
      <div class="title">
        <div class="label-l">All models</div>
        <div class="subtitle label-s">Select the models you want to have as trade-offs</div>
      </div>
      <div class="scroller-wrapper">
        <Simplebar class="all-models-scroller">
          <table class="models-table">
            <thead>
              <tr>
                <th v-if="!isReadOnly"></th>
                <th class="label-s">Report name</th>
                <th v-for="m in metricsNames" :key="m" class="label-s">
                  {{ metricsVerboseNamesMap.get(m) }}
                </th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="r in reports"
                :key="r.id"
                class="report"
                @mouseover="onMouseHoverReport(r.id)"
                @mouseleave="onMouseOut"
                :class="{ hover: hoveredModelId === r.id }"
              >
                <td v-if="!isReadOnly">
                  <SimpleButton
                    :label="selectedModelsIds.includes(r.id) ? '-' : '+'"
                    class="toggle-model"
                    :class="selectedModelsIds.includes(r.id) ? 'remove' : 'add'"
                    kind="tertiary"
                    @click="toggleModel(r.id)"
                  />
                </td>
                <td class="paragraph-s">{{ r.key }}</td>
                <td v-for="m in metricsNames" :key="m" class="paragraph-s">
                  <!-- use a loop to avoid calling getFormattedReportMetricByName multiple times -->
                  <template
                    v-for="(difference, index) in [getFormattedMetricDifference(r, m)]"
                    :key="index"
                  >
                    <span
                      v-if="isNaN(getReportMetricByName(r, m)) && (m == xMetric || m == yMetric)"
                      class="warning"
                    >
                      <FloatingTooltip
                        text="This metric has not been calculated, this model will not appear in the plot."
                      >
                        <i class="icon icon-warning" />
                      </FloatingTooltip>
                    </span>
                    {{ getFormattedReportMetricByName(r, m) }}
                    <span
                      v-if="difference.value !== 0 && !isNaN(difference.value)"
                      class="difference"
                      :class="{ better: difference.better }"
                    >
                      {{ difference.stringValue }}
                    </span>
                  </template>
                </td>
                <td>
                  <RouterLink
                    class="skore-button tertiary"
                    icon-left="icon-external-link"
                    :to="{
                      name: RouteNames.EstimatorReportDetail,
                      params: { tenantName, projectName, reportId: r.id },
                    }"
                    target="_blank"
                  >
                    <i class="icon left icon-external-link" />
                  </RouterLink>
                </td>
              </tr>
            </tbody>
          </table>
        </Simplebar>
      </div>
    </div>
  </div>
</template>

<style scoped>
.estimator-report-comparison {
  display: grid;
  height: 100%;
  gap: var(--spacing-32);
  grid-template:
    "top-left top-right" 50%
    "bottom bottom" 50% / 1fr 1fr;

  .block-title {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: var(--spacing-16);

    & > div {
      display: flex;
      flex-direction: row;
      align-items: center;
      gap: var(--spacing-4);
    }

    .title-icon {
      display: inline-flex;
      width: 32px;
      height: 32px;
      align-items: center;
      justify-content: center;
      border-radius: var(--radius-s);
      margin-right: var(--spacing-8);
      background-color: var(--color-button-background-tertiary-hover);
    }
  }

  .plot {
    z-index: 2;
    display: flex;
    height: 100%;
    flex-direction: column;
    grid-area: top-left;

    .plot-container {
      position: relative;
      height: 100%;
      flex-grow: 1;
      padding: var(--spacing-8) var(--spacing-8) var(--spacing-4) var(--spacing-4);
      border: solid var(--stroke-width-m) var(--color-stroke-background-primary);
      border-radius: var(--radius-xs);

      .plot-tooltip {
        position: absolute;
        top: 0;
        left: 0;
        width: max-content;
        padding: var(--spacing-6);
        border-radius: var(--radius-xs);
        background-color: var(--color-tooltip-background);
        box-shadow: var(--box-shadow-l);
        color: var(--color-button-text-primary);
        transition: transform var(--animation-duration) var(--animation-easing);
      }
    }
  }

  .selected-models {
    display: flex;
    flex-direction: column;
    grid-area: top-right;

    .selected-models-container {
      max-height: calc(100% - 32px - var(--spacing-16));
      flex-grow: 1;

      .placeholder {
        display: flex;
        height: 100%;
        flex-direction: column;
        align-items: center;
        justify-content: center;
        color: var(--color-text-secondary);
        text-align: center;

        .label-s {
          color: var(--color-text-secondary);
        }
      }

      .scroller {
        height: 100%;
        overflow-x: hidden;
      }

      .selected-models-list {
        display: flex;
        flex-direction: column;
        gap: var(--spacing-16);
      }

      .selected-model-container {
        .selected-model {
          display: flex;
          flex-direction: column;
          padding: var(--spacing-12);
          border: var(--stroke-width-m) solid var(--color-stroke-background-primary);
          border-radius: var(--radius-s);
          gap: var(--spacing-16);
          transition: border-color var(--animation-duration) var(--animation-easing);

          &.hover {
            border-color: hsl(29deg 100% 59%);
          }

          .name-and-score {
            display: flex;
            flex-flow: row wrap;
            align-items: center;
            justify-content: space-between;

            .name {
              padding: var(--spacing-4) var(--spacing-6);
              border: var(--stroke-width-m) solid var(--color-stroke-background-primary);
              border-radius: var(--radius-xs);
              font-family: GeistMono, monospace;

              .icon {
                color: var(--color-text-tertiary);
                vertical-align: middle;
              }
            }

            .metrics {
              display: flex;
              gap: var(--spacing-12);

              span {
                color: var(--color-text-secondary);
              }
            }
          }

          .explanation {
            display: flex;
            flex-direction: row;
            align-items: baseline;
            gap: var(--spacing-8);

            .text {
              display: -webkit-box;
              overflow: hidden;
              max-height: calc(var(--root-font-size) * 1.5 * 3);
              -webkit-box-orient: vertical;
              -webkit-line-clamp: 3;
              line-clamp: 3;
            }
          }
        }
      }
    }
  }

  .all-models {
    display: flex;
    height: 100%;
    flex-direction: column;
    padding-bottom: var(--spacing-32);
    grid-area: bottom;

    .subtitle {
      margin-bottom: var(--spacing-16);
      color: var(--color-text-secondary);
    }

    .scroller-wrapper {
      overflow: hidden;
      max-height: 100%;
      flex-grow: 1;
      border-radius: var(--radius-xs);
    }

    .all-models-scroller {
      max-height: 100%;
    }

    .models-table {
      width: 100%;
      border-collapse: separate;
      border-spacing: 0;
      table-layout: auto;

      thead {
        position: sticky;
        top: 0;
        background-color: var(--color-background-primary);

        tr {
          color: var(--color-text-secondary);

          th {
            padding: var(--spacing-6) var(--spacing-8);
            border-right: var(--stroke-width-m) solid var(--color-stroke-background-primary);
            background-color: var(--color-background-tertiary);
            text-align: left;

            &:first-child {
              border-top-left-radius: var(--radius-xs);
            }

            &:last-child {
              border-right: none;
              border-top-right-radius: var(--radius-xs);
            }
          }
        }
      }

      tbody {
        tr {
          background-color: var(--color-background-primary);
          transition: background-color var(--animation-duration) var(--animation-easing);

          td {
            padding: var(--spacing-8);
            border-width: var(--stroke-width-m);
            border-style: none solid solid none;
            border-color: var(--color-stroke-background-primary);

            &:first-child {
              border-left-style: solid;
              border-left-color: var(--color-background-tertiary);
              text-align: center;
            }

            &:last-child {
              border-right-color: var(--color-background-tertiary);
              text-align: center;
            }
          }

          &:last-child td:first-child {
            border-bottom-left-radius: var(--radius-xs);
          }

          &:last-child td:last-child {
            border-bottom-right-radius: var(--radius-xs);
          }

          &.hover {
            background-color: hsl(29deg 100% 59% / 8%);
          }
        }
      }
    }

    .toggle-model {
      &.remove {
        color: var(--color-negative);
      }

      &.add {
        color: var(--color-positive);
      }
    }

    .difference {
      color: var(--color-negative);

      &.better {
        color: var(--color-positive);
      }
    }
  }
}

@supports (animation-timeline: scroll()) {
  .models-table thead {
    animation: scroll-shadow linear both;
    animation-range: 0 calc(32px + (var(--spacing-8) * 2));
    animation-timeline: scroll();
  }

  @keyframes scroll-shadow {
    from {
      box-shadow: none;
    }

    to {
      box-shadow:
        0 4px 8px -2px rgb(16 24 40 / 10%),
        0 2px 4px -2px rgb(16 24 40 / 6%);
    }
  }
}
</style>
